import { useAiChatContext } from 'juneau';
import { Button } from '@insio/ui-toolkit';
import { usePermissions } from '@/features/auth';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { APPROVE_PERMISSIONS } from './chat-content';
import { TappeerWordmark } from './TappeerBrandMark';
import { useAiChatSimplifiedMode } from './use-ai-chat-simplified-mode';

/**
 * "Let's get started" hero shown while the conversation is empty.
 * Compact mode trims the spacing so it fits the narrower side panel.
 */
export function GetStartedState({ compact = false }: { compact?: boolean }) {
  const t = useFeatureTranslation('aiChat');
  const { sendMessageWithText } = useAiChatContext();
  const simplifiedMode = useAiChatSimplifiedMode();
  const { hasPermission } = usePermissions();
  // Nothing ever waits on somebody who cannot approve; their own documents are what they follow.
  const canApprove = APPROVE_PERMISSIONS.some(hasPermission);
  const workSuggestion = canApprove
    ? t('emptyState.suggestionApprovals')
    : t('emptyState.suggestionMyRequests');

  // Simplified-mode accounts approve and track their documents in INSIO.
  const suggestions = [
    t('emptyState.suggestionOverdue'),
    ...(simplifiedMode ? [] : [workSuggestion]),
    t('emptyState.suggestionSpend')
  ];

  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center text-center ${
        compact ? 'gap-md px-md py-lg' : 'gap-lg px-lg py-2xl'
      }`}
    >
      <TappeerWordmark width={compact ? 132 : 180} />
      <div className='flex flex-col gap-xs'>
        <h2 className={`font-semibold text-foreground-base ${compact ? 'text-base' : 'text-xl'}`}>
          {t('emptyState.title')}
        </h2>
        <p className={`text-foreground-muted ${compact ? 'max-w-xs text-xs' : 'max-w-md text-sm'}`}>
          {t('emptyState.subtitle')}
        </p>
      </div>
      <div className='flex flex-wrap items-center justify-center gap-sm'>
        {suggestions.map(suggestion => (
          <Button
            key={suggestion}
            variant='default'
            size='sm'
            className='rounded-full border border-border-base'
            onClick={() => void sendMessageWithText(suggestion)}
          >
            {suggestion}
          </Button>
        ))}
      </div>
    </div>
  );
}
